import React, { useEffect, useRef } from 'react';
import { PhoneOff, Mic, MicOff, Video, VideoOff, Monitor, ChevronDown, Clock } from 'lucide-react';
import { useVoiceStore } from '../../store/voiceStore';

const API = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// ── Inline video tile for DM call ────────────────────────────────────────────
const VideoTile: React.FC<{ stream: MediaStream; username: string; source: 'camera' | 'screen' }> = ({
  stream, username, source,
}) => {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.srcObject = stream;
  }, [stream]);
  return (
    <div className="relative rounded-lg overflow-hidden bg-black aspect-video w-full">
      <video ref={ref} autoPlay playsInline className="w-full h-full object-cover" />
      <div className="absolute bottom-1 left-2 flex items-center gap-1">
        {source === 'screen'
          ? <Monitor className="w-3 h-3 text-white/70" />
          : <Video className="w-3 h-3 text-white/70" />}
        <span className="text-white/70 text-[10px]">{username}</span>
      </div>
    </div>
  );
};

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

const CallExpandedPanel: React.FC = () => {
  const {
    dmCallStatus,
    dmCallUsers,
    dmCallSpeakingStates,
    dmCallVideoTracks,
    isMuted,
    isCameraOn,
    isScreenSharing,
    callDuration,
    toggleMute,
    toggleCamera,
    toggleScreenShare,
    leaveDmCall,
    setCallPanelExpanded,
    isCallPanelExpanded,
  } = useVoiceStore();

  const panelRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setCallPanelExpanded(false);
      }
    };
    if (isCallPanelExpanded) {
      document.addEventListener('mousedown', handler);
    }
    return () => document.removeEventListener('mousedown', handler);
  }, [isCallPanelExpanded, setCallPanelExpanded]);

  if (!isCallPanelExpanded || dmCallStatus === 'idle') return null;

  return (
    <div
      ref={panelRef}
      className="absolute bottom-full left-0 right-0 mb-1 mx-1 bg-bg-secondary/95 backdrop-blur-xl border border-border-color rounded-xl shadow-2xl shadow-black/40 overflow-hidden animate-slide-up z-50"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-border-color">
        <div className="flex items-center gap-2">
          <div
            className={`w-2 h-2 rounded-full ${
              dmCallStatus === 'connected' ? 'bg-success' : 'bg-warning animate-pulse'
            }`}
          />
          <span className="text-xs font-semibold text-text-primary">
            {dmCallStatus === 'dialing' ? 'Appel en cours...' : 'Appel DM'}
          </span>
          {dmCallStatus === 'connected' && (
            <span className="flex items-center gap-1 text-xs text-text-secondary">
              <Clock className="w-3 h-3" />
              {formatDuration(callDuration)}
            </span>
          )}
        </div>
        <button
          onClick={() => setCallPanelExpanded(false)}
          className="p-1 rounded hover:bg-bg-tertiary text-text-secondary transition-colors"
        >
          <ChevronDown className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Remote video tiles (camera or screen share from the other participant) */}
      {Object.entries(dmCallVideoTracks).length > 0 && (
        <div className="px-2 pt-2 pb-0 space-y-1.5">
          {Object.entries(dmCallVideoTracks).map(([userId, { stream, source }]) => {
            const user = dmCallUsers.find((u) => u.id === userId);
            return (
              <VideoTile
                key={userId}
                stream={stream}
                username={user?.username ?? userId}
                source={source}
              />
            );
          })}
        </div>
      )}

      {/* Participants */}
      <div className="px-2 py-2 max-h-48 overflow-y-auto space-y-0.5">
        {dmCallUsers.length === 0 && dmCallStatus === 'dialing' && (
          <div className="px-2 py-3 text-xs text-text-secondary text-center">
            En attente de réponse...
          </div>
        )}
        {dmCallUsers.map((user) => {
          const level = dmCallSpeakingStates[user.id] || 0;
          const isSpeaking = level > 0.05;
          return (
            <div
              key={user.id}
              className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-bg-tertiary/50 transition-colors"
            >
              <div className="relative flex-shrink-0">
                <div
                  className={`w-7 h-7 rounded-full overflow-hidden border-2 transition-colors ${
                    isSpeaking ? 'border-success' : 'border-transparent'
                  }`}
                >
                  {user.avatar_url ? (
                    <img
                      src={`${API}${user.avatar_url}`}
                      alt={user.username}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-accent-color flex items-center justify-center text-white font-bold text-[10px]">
                      {user.username.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
              </div>
              <span className="text-xs text-text-primary flex-1 truncate">
                {user.username}
              </span>
              {user.muted && <MicOff className="w-3 h-3 text-error flex-shrink-0" />}
            </div>
          );
        })}
      </div>

      {/* Controls */}
      <div className="flex items-center justify-center gap-3 px-3 py-2.5 border-t border-border-color">
        <button
          onClick={toggleMute}
          className={`p-2 rounded-full transition-all ${
            isMuted
              ? 'bg-error/20 text-error hover:bg-error/30'
              : 'bg-bg-tertiary text-text-secondary hover:bg-bg-tertiary/80 hover:text-text-primary'
          }`}
          title={isMuted ? 'Réactiver le micro' : 'Couper le micro'}
        >
          {isMuted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
        </button>

        {/* Camera toggle */}
        <button
          onClick={() => void toggleCamera()}
          className={`p-2 rounded-full transition-all ${
            isCameraOn
              ? 'bg-accent-color/20 text-accent-color hover:bg-accent-color/30'
              : 'bg-bg-tertiary text-text-secondary hover:bg-bg-tertiary/80 hover:text-text-primary'
          }`}
          title={isCameraOn ? 'Couper la caméra' : 'Activer la caméra'}
        >
          {isCameraOn ? <Video className="w-4 h-4" /> : <VideoOff className="w-4 h-4" />}
        </button>

        {/* Screen share toggle */}
        <button
          onClick={() => void toggleScreenShare()}
          className={`p-2 rounded-full transition-all ${
            isScreenSharing
              ? 'bg-success/20 text-success hover:bg-success/30'
              : 'bg-bg-tertiary text-text-secondary hover:bg-bg-tertiary/80 hover:text-text-primary'
          }`}
          title={isScreenSharing ? "Arrêter le partage" : "Partager l'écran"}
        >
          <Monitor className="w-4 h-4" />
        </button>

        <button
          onClick={leaveDmCall}
          className="p-2 rounded-full bg-error hover:bg-error/90 text-white transition-all hover:scale-105 active:scale-95"
          title="Raccrocher"
        >
          <PhoneOff className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default CallExpandedPanel;
